import React, { useState, useEffect, useCallback } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { ChevronDown, Upload, Info, Plus, X, ArrowLeft } from "lucide-react";

export const SOPCreationForm = () => {
  // Navigation function - replace with your actual navigation logic
  const navigate = (path) => {
    // For demo purposes, using window.location
    // In your actual app, use: navigate(path) from react-router-dom
    if (path === -1) {
      window.history.back();
    } else {
      window.location.href = path;
    }
  };
  const [selectedSOPType, setSelectedSOPType] = useState("");
  const [selectedSOPSubType, setSelectedSOPSubType] = useState("");
  const [showCustomSOPModal, setShowCustomSOPModal] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Mock data - replace with your actual data hooks
  const sopTypeOptions = [
    { value: "operational", label: "Operational SOP" },
    { value: "safety", label: "Safety SOP" },
    { value: "quality", label: "Quality SOP" },
    { value: "maintenance", label: "Maintenance SOP" },
  ];

  const sopSubTypeOptions = selectedSOPType
    ? [
        { value: "subtype1", label: `${selectedSOPType} Sub-Type 1` },
        { value: "subtype2", label: `${selectedSOPType} Sub-Type 2` },
        { value: "subtype3", label: `${selectedSOPType} Sub-Type 3` },
      ]
    : [];

  // Main SOP Creation Form Fields
  const mainFormFields: FormField[] = [
    {
      name: "sopType",
      type: "select",
      label: "Select SOP Type",
      required: true,
      placeholder: "Select a type...",
      options: sopTypeOptions,
      size: "medium",
      variant: "outlined",
      className: "col-span-1",
    },
    {
      name: "sopSubType",
      type: "select",
      label: "Select SOP Sub-Type",
      required: true,
      placeholder: selectedSOPType
        ? "Select a sub-type..."
        : "First select a type...",
      options: sopSubTypeOptions,
      size: "medium",
      variant: "outlined",
      disabled: !selectedSOPType,
      className: "col-span-1",
    },
    {
      name: "uploadedFile",
      type: "file",
      label: "Upload SOP File",
      accept: ".xlsx,.csv",
      multiple: false,
      size: "medium",
      helperText:
        "Only .xlsx and .csv formats accepted. Upload the completed file to review valid and invalid rows.",
      required: true,
      customValidation: (value) => {
        if (value && value[0]) {
          const file = value[0];
          const validExtensions = [".xlsx", ".csv"];
          const fileExtension = "." + file.name.split(".").pop().toLowerCase();
          if (!validExtensions.includes(fileExtension)) {
            return "Please upload only .xlsx or .csv files";
          }
        }
        return true;
      },
      className: "col-span-2",
      classes: {
        file: "border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors",
      },
    },
  ];

  // Custom SOP Form Fields
  const customSOPFields: FormField[] = [
    {
      name: "customSOPType",
      type: "text",
      label: "Custom SOP Type",
      required: true,
      placeholder: "Enter SOP type...",
      minLength: 1,
      maxLength: 50,
      size: "medium",
      variant: "outlined",
      className: "col-span-2",
    },
    {
      name: "customSOPSubType",
      type: "text",
      label: "Custom SOP Sub-Type",
      required: false,
      placeholder: "Enter SOP sub-type...",
      minLength: 1,
      maxLength: 50,
      size: "medium",
      variant: "outlined",
      className: "col-span-2",
    },
    {
      name: "description",
      type: "textarea",
      label: "Description (Optional)",
      placeholder: "Describe what this template will be used for...",
      rows: 4,
      maxLength: 500,
      size: "medium",
      variant: "outlined",
      className: "col-span-2",
    },
  ];

  // Main form submission
  const handleMainFormSubmit = async (data) => {
    try {
      setFormSubmitting(true);

      const { uploadedFile, ...rest } = data;

      const formData = new FormData();
      Object.entries(rest).forEach(([key, value]) => {
        formData.append(key, String(value));
      });

      // Handle file upload
      if (uploadedFile) {
        const file = uploadedFile?.[0] || uploadedFile?.file || uploadedFile;
        if (file instanceof File) {
          formData.append("sop_file", file);
        }
      }

      // Add your API call here
      // await createSOP(formData);

      // Navigate to success page or back to previous page
      navigate("/service-orders/wx-master"); // Adjust route as needed
    } catch (error) {
      alert("Error creating SOP. Please try again.");
    } finally {
      setFormSubmitting(false);
    }
  };

  // Custom SOP submission
  const handleCustomSOPSubmit = async (data) => {
    try {
      // Add your API call here to save custom template
      // await createCustomSOPTemplate(data);

      // Add the new template to available options
      sopTypeOptions.push({
        value: data.customSOPType.toLowerCase().replace(/\s+/g, "_"),
        label: data.customSOPType,
      });

      setShowCustomSOPModal(false);
      customSOPForm.reset();
    } catch (error) {
      alert("Error creating custom SOP template. Please try again.");
    }
  };

  // Field change handlers
  const handleMainFieldChange = (field: string, data: any) => {
    switch (field) {
      case "sopType":
        setSelectedSOPType(data);
        setSelectedSOPSubType("");
        mainForm.setValue("sopSubType", ""); // Clear sub-type when type changes
        break;
      case "sopSubType":
        setSelectedSOPSubType(data);
        break;
    }
  };

  const handleCustomFieldChange = (field: string, data: any) => {
    // Handle any custom field changes if needed
  };

  // Reset handlers
  const handleMainReset = () => {
    setSelectedSOPType("");
    setSelectedSOPSubType("");
  };

  const handleCustomReset = () => {
    // Reset custom form if needed
  };

  // Modal handlers
  const handleAddCustomSOP = () => {
    setShowCustomSOPModal(true);
  };

  const handleCloseCustomSOPModal = () => {
    setShowCustomSOPModal(false);
    customSOPForm.reset();
  };

  const handleCancel = () => {
    navigate("/service-orders/wx-master"); // Go back to master page
  };

  // Form configurations
  const mainFormConfig: FormConfig = {
    fields: mainFormFields,
    shouldUnregister: true,
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      label: "text-sm text-gray-800 font-semibold",
      input: "rounded-md border px-3 py-2 border-gray-300 w-full",
      select: "rounded-md border px-3 py-2 border-gray-300",
      textarea: "rounded-md border px-3 py-2 border-gray-300",
      file: "border-2 border-dashed border-gray-300 rounded-lg p-8 text-center",
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
  };

  const customSOPFormConfig: FormConfig = {
    fields: customSOPFields,
    shouldUnregister: true,
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      label: "text-sm text-gray-800 font-semibold",
      input: "rounded-md border px-3 py-2 border-gray-300 w-full",
      textarea: "rounded-md border px-3 py-2 border-gray-300",
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
  };

  // Create forms
  const mainForm = FormService.createForm(mainFormConfig);
  const customSOPForm = FormService.createForm(customSOPFormConfig);

  // Custom footer for custom SOP form
  const renderCustomSOPFooter = () => (
    <div className="flex justify-end gap-3 mt-6">
      <button
        type="button"
        onClick={handleCloseCustomSOPModal}
        className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={customSOPForm.handleSubmit(handleCustomSOPSubmit)}
        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
      >
        Save Custom Template
      </button>
    </div>
  );

  return (
    <>
      {/* Main Page Content */}
      <div className="min-h-screen ">
        <div className="w-full mx-auto p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => navigate("/service-orders/wx-master")}
                className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-1" />
                Back
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-2xl font-bold text-gray-900">
                Bulk Upload SOP
              </h1>
            </div>
            <p className="text-gray-600">
              Create and upload Standard Operating Procedures in bulk using our
              template system.
            </p>
          </div>

          {/* Step Cards */}
          <div className="space-y-6">
            {/* Step 1: Select or Create Template Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="flex items-center justify-center w-6 h-6 bg-blue-500 text-white text-sm font-medium rounded-full mr-3">
                  1
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Select or Create Template
                </h2>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Choose the appropriate template for your SOP or create a custom
                one.
              </p>

              <DynamicForm
                fields={mainFormFields.slice(0, 2)} // Only SOP Type and Sub-Type fields
                form={mainForm}
                gridColumns={2}
                responsiveColumns={{
                  sm: 1,
                  md: 2,
                }}
                config={mainFormConfig}
                onSubmit={handleMainFormSubmit}
                onReset={handleMainReset}
                loading={formSubmitting}
                onFieldChange={handleMainFieldChange}
                renderCustomFooter={() => null} // No footer for this section
              />

              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleAddCustomSOP}
                  className="flex items-center text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Custom SOP
                </button>
              </div>
            </div>

            {/* Step 2: Fill & Save Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="flex items-center justify-center w-6 h-6 bg-blue-500 text-white text-sm font-medium rounded-full mr-3">
                  2
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Fill & Save
                </h2>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Complete the Excel file using your SOP data. Make sure there
                are:
              </p>

              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  No blank required fields
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  No duplicate SOP names
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  Correct data formats (e.g., numbers for SLA, costs)
                </li>
              </ul>
            </div>

            {/* Step 3: Upload File Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="flex items-center justify-center w-6 h-6 bg-blue-500 text-white text-sm font-medium rounded-full mr-3">
                  3
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Upload File
                </h2>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Upload the completed file below. You will get a preview to
                review valid and invalid rows.
              </p>

              <DynamicForm
                fields={[mainFormFields[2]]} // Only the file upload field
                form={mainForm}
                gridColumns={1}
                config={mainFormConfig}
                onSubmit={handleMainFormSubmit}
                onReset={handleMainReset}
                loading={formSubmitting}
                onFieldChange={handleMainFieldChange}
                renderCustomFooter={() => null} // No footer for this section
              />
            </div>

            {/* Important Notes Card */}
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
              <div className="flex items-start">
                <Info className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">
                    Important Notes
                  </h3>
                  <ul className="space-y-1 text-sm text-blue-800">
                    <li>• Invalid rows will be flagged with error messages</li>
                    <li>• You can download an error report and retry</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                disabled={formSubmitting}
                className="px-6 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={mainForm.handleSubmit(handleMainFormSubmit)}
                disabled={formSubmitting}
                className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {formSubmitting ? "Processing..." : "Next Step"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom SOP Template Dialog */}
      <Dialog
        open={showCustomSOPModal}
        onOpenChange={(open) => !open && handleCloseCustomSOPModal()}
      >
        <DialogContent className="w-full max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Add Custom SOP Template
            </DialogTitle>
          </DialogHeader>

          <div>
            <p className="text-sm text-gray-600 mb-6">
              Create a custom SOP template for your specific needs.
            </p>

            <DynamicForm
              fields={customSOPFields}
              form={customSOPForm}
              gridColumns={1}
              config={customSOPFormConfig}
              onSubmit={handleCustomSOPSubmit}
              onReset={handleCustomReset}
              loading={false}
              onFieldChange={handleCustomFieldChange}
              renderCustomFooter={renderCustomSOPFooter}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SOPCreationForm;
